import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChatBubbleLeftEllipsisIcon,
  HeartIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { Timestamp } from "firebase/firestore";
import {
  GpsRouteSlide,
  HeartRateZonesSlide,
  SessionDetailsSlide,
} from "./AdminPostSlides";
import { getUserPublicData } from "../services/userServices";
import {
  fetchAllGlobalTypesGrouped,
  fetchGlobalTypesByCategory,
  fetchUserActivityTypes,
  getActivityLog,
  getPerformanceLog,
  getSession,
  getTrainingLogLinks,
  recoverGlobalActivityType,
  recoverUserActivityType,
} from "../services/postLinkedDataService";
import { ActivityType } from "../types/activity";
import {
  ActivityLog,
  ActivityMeasurement,
  PerformanceLog,
  Session,
} from "../types/postActivity";
import { SocialPost } from "../types/socialPost";

interface AdminPostCardProps {
  post: SocialPost;
  /** When true, omit outer card chrome for embedding in a modal. */
  embedded?: boolean;
}

type MediaSlide =
  | { type: "image"; content: string }
  | { type: "gps"; content: ActivityLog }
  | { type: "heartRate"; content: ActivityLog }
  | {
      type: "session";
      content: { measurements: ActivityMeasurement[] };
    };

const MAX_TEXT_LENGTH = 200;

const formatDate = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return "Unknown date";
  const date = timestamp.toDate?.() ?? new Date();
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatRelativeTime = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return "";
  const date = timestamp.toDate?.() ?? new Date();
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWeek = Math.floor(diffDay / 7);

  if (diffSec < 60) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  if (diffWeek < 52) return `${diffWeek}w`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const shouldTruncateText = (text: string) =>
  Boolean(text && text.length > MAX_TEXT_LENGTH);

const getTruncatedText = (text: string) => {
  if (!shouldTruncateText(text)) return text;
  const truncated = text.substring(0, MAX_TEXT_LENGTH);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  return lastSpaceIndex > 0
    ? truncated.substring(0, lastSpaceIndex)
    : truncated;
};

const getRawActivityId = (activityTypeId: string): string => {
  if (activityTypeId.startsWith("global_")) {
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) return parts.slice(2).join("_");
  }
  return activityTypeId;
};

const getGlobalCategoryIdFromActivityTypeId = (
  activityTypeId: string,
): string | undefined => {
  if (!activityTypeId.startsWith("global_")) return undefined;
  const parts = activityTypeId.split("_");
  if (parts.length >= 3) return parts[1];
  return undefined;
};

/**
 * Read-only PeakPWA PostCard clone for admin monitoring.
 * Includes GPS route, heart-rate zones, session details, and performance
 * blocks — without like/comment/edit/delete/share/unfollow/report actions.
 */
const AdminPostCard: React.FC<AdminPostCardProps> = ({
  post,
  embedded = false,
}) => {
  const postOwnerId = post.userID;
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showComments, setShowComments] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [commentProfiles, setCommentProfiles] = useState<
    Record<string, { username: string; imageUrl?: string }>
  >({});

  const [activityLog, setActivityLog] = useState<ActivityLog | null>(null);
  const [session, setSession] = useState<(Session & { id: string }) | null>(
    null,
  );
  const [performanceLog, setPerformanceLog] = useState<PerformanceLog | null>(
    null,
  );
  const [linkedActivityLogID, setLinkedActivityLogID] = useState<string | null>(
    null,
  );
  const [linkedSessionID, setLinkedSessionID] = useState<string | null>(null);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, ActivityType[]>
  >({});
  const [hasAttemptedGlobalFallback, setHasAttemptedGlobalFallback] =
    useState(false);
  const [linkedLoading, setLinkedLoading] = useState(false);

  const resolvedActivityLogID = post.activityLogID || linkedActivityLogID;
  const resolvedSessionID = post.sessionID || linkedSessionID;

  const hasGPSData = Boolean(
    activityLog?.positionData?.position_samples &&
      activityLog.positionData.position_samples.length > 0,
  );
  const hasHeartRateData = Boolean(
    activityLog?.heartRateData?.summary?.hr_zone_data &&
      activityLog.heartRateData.summary.hr_zone_data.length > 0,
  );
  const hasSessionDetails = Boolean(
    session?.ActivityMeasurementList &&
      session.ActivityMeasurementList.length > 0,
  );
  const mediaList = useMemo(
    () => (Array.isArray(post.mediaList) ? post.mediaList.filter(Boolean) : []),
    [post.mediaList],
  );

  const getActivityTypeName = (
    activity: string | ActivityMeasurement,
  ): string => {
    const activityTypeId =
      typeof activity === "string" ? activity : activity.ActivityTypeID;
    const categoryId =
      typeof activity !== "string"
        ? activity.globalCategoryID ||
          getGlobalCategoryIdFromActivityTypeId(activityTypeId)
        : getGlobalCategoryIdFromActivityTypeId(activityTypeId);
    const rawActivityId = getRawActivityId(activityTypeId);

    const userActivityType = activityTypes.find(
      (type) => type.id === rawActivityId,
    );
    if (userActivityType) return userActivityType.name;

    if (categoryId) {
      const packActivityType = (globalActivityTypes[categoryId] || []).find(
        (type) => type.id === rawActivityId,
      );
      if (packActivityType) return packActivityType.name;
    }

    for (const categoryTypes of Object.values(globalActivityTypes)) {
      const packActivityType = categoryTypes.find(
        (type) => type.id === rawActivityId,
      );
      if (packActivityType) return packActivityType.name;
    }

    if (typeof activity !== "string" && activity.activityName) {
      return activity.activityName;
    }

    return "Unknown Activity";
  };

  // Resolve linked IDs from training log when post doesn't carry them.
  useEffect(() => {
    const resolveLinks = async () => {
      const needsActivity = !post.activityLogID;
      const needsSession = !post.sessionID;
      if (
        !postOwnerId ||
        !post.trainingLogID ||
        (!needsActivity && !needsSession)
      ) {
        setLinkedActivityLogID(null);
        setLinkedSessionID(null);
        return;
      }
      try {
        const links = await getTrainingLogLinks(
          postOwnerId,
          post.trainingLogID,
        );
        setLinkedActivityLogID(links?.linkedActivityLogID || null);
        setLinkedSessionID(links?.linkedSessionID || null);
      } catch (err) {
        console.error("Error resolving training log links:", err);
        setLinkedActivityLogID(null);
        setLinkedSessionID(null);
      }
    };
    resolveLinks();
  }, [postOwnerId, post.trainingLogID, post.activityLogID, post.sessionID]);

  useEffect(() => {
    const loadActivity = async () => {
      if (!resolvedActivityLogID || !postOwnerId) {
        setActivityLog(null);
        return;
      }
      try {
        setLinkedLoading(true);
        const log = await getActivityLog(postOwnerId, resolvedActivityLogID);
        setActivityLog(log);
      } catch (err) {
        console.error("Error fetching activity log:", err);
        setActivityLog(null);
      } finally {
        setLinkedLoading(false);
      }
    };
    loadActivity();
  }, [resolvedActivityLogID, postOwnerId]);

  useEffect(() => {
    const loadSession = async () => {
      if (!resolvedSessionID || !postOwnerId) {
        setSession(null);
        setActivityTypes([]);
        return;
      }
      try {
        setLinkedLoading(true);
        const sessionData = await getSession(postOwnerId, resolvedSessionID);
        setSession(sessionData);
        if (!sessionData) return;

        const activityTypesData = await fetchUserActivityTypes(postOwnerId);
        const sessionMeasurements = sessionData.ActivityMeasurementList || [];
        const requiredCustomIds = Array.from(
          new Set(
            sessionMeasurements
              .filter((m) => !m.globalCategoryID)
              .map((m) => getRawActivityId(m.ActivityTypeID)),
          ),
        );
        const loadedIds = new Set(
          activityTypesData.map((t) => t.id).filter(Boolean),
        );
        const missingIds = requiredCustomIds.filter((id) => !loadedIds.has(id));

        if (missingIds.length > 0) {
          const recovered = await Promise.all(
            missingIds.map((id) => recoverUserActivityType(postOwnerId, id)),
          );
          const merged = [
            ...activityTypesData,
            ...recovered.filter((t): t is ActivityType => !!t),
          ];
          setActivityTypes(
            Array.from(
              new Map(
                merged.filter((t) => !!t.id).map((t) => [t.id, t]),
              ).values(),
            ),
          );
        } else {
          setActivityTypes(activityTypesData);
        }
      } catch (err) {
        console.error("Error fetching session:", err);
        setSession(null);
      } finally {
        setLinkedLoading(false);
      }
    };
    loadSession();
  }, [resolvedSessionID, postOwnerId]);

  useEffect(() => {
    const loadPerformance = async () => {
      const perfLogID =
        session?.linkedPerformanceLogID || post.performanceLogID;
      if (!perfLogID || !postOwnerId) {
        setPerformanceLog(null);
        return;
      }
      try {
        const log = await getPerformanceLog(postOwnerId, perfLogID);
        setPerformanceLog(log);
      } catch (err) {
        console.error("Error fetching performance log:", err);
        setPerformanceLog(null);
      }
    };
    loadPerformance();
  }, [postOwnerId, session?.linkedPerformanceLogID, post.performanceLogID]);

  useEffect(() => {
    if (!session?.ActivityMeasurementList?.length) return;

    const categoryIds = Array.from(
      new Set(
        session.ActivityMeasurementList.map((measurement) => {
          return (
            measurement.globalCategoryID ||
            getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID)
          );
        }).filter((id): id is string => !!id),
      ),
    );
    if (categoryIds.length === 0) return;

    const fetchGlobalTypes = async () => {
      try {
        const entries = await Promise.all(
          categoryIds.map(async (categoryId) => {
            const types = await fetchGlobalTypesByCategory(categoryId);
            return [categoryId, types] as const;
          }),
        );
        const next = Object.fromEntries(entries) as Record<
          string,
          ActivityType[]
        >;

        const requiredGlobalMap = new Map<
          string,
          { categoryId: string; rawActivityId: string }
        >();
        (session.ActivityMeasurementList || []).forEach((measurement) => {
          const categoryId =
            measurement.globalCategoryID ||
            getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
          if (!categoryId) return;
          const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
          requiredGlobalMap.set(`${categoryId}::${rawActivityId}`, {
            categoryId,
            rawActivityId,
          });
        });

        const missingRefs = Array.from(requiredGlobalMap.values()).filter(
          (ref) =>
            !(next[ref.categoryId] || []).some(
              (type) => type.id === ref.rawActivityId,
            ),
        );

        if (missingRefs.length > 0) {
          const recovered = await Promise.all(
            missingRefs.map(async (ref) => {
              const type = await recoverGlobalActivityType(
                ref.categoryId,
                ref.rawActivityId,
              );
              return type
                ? { categoryId: ref.categoryId, type }
                : null;
            }),
          );
          recovered.forEach((entry) => {
            if (!entry) return;
            const list = next[entry.categoryId] || [];
            if (!list.some((t) => t.id === entry.type.id)) {
              next[entry.categoryId] = [...list, entry.type];
            }
          });
        }

        setGlobalActivityTypes((prev) => ({ ...prev, ...next }));
      } catch (err) {
        console.error("Error fetching global activity types:", err);
      }
    };

    fetchGlobalTypes();
  }, [session]);

  useEffect(() => {
    setHasAttemptedGlobalFallback(false);
  }, [session?.id]);

  useEffect(() => {
    if (!session?.ActivityMeasurementList?.length) return;
    if (hasAttemptedGlobalFallback) return;

    const hasUnknown = session.ActivityMeasurementList.some(
      (m) => getActivityTypeName(m) === "Unknown Activity",
    );
    if (!hasUnknown) return;

    const fallback = async () => {
      try {
        setHasAttemptedGlobalFallback(true);
        const grouped = await fetchAllGlobalTypesGrouped();
        setGlobalActivityTypes((prev) => ({ ...prev, ...grouped }));
      } catch (err) {
        console.error("Error fetching fallback global activity types:", err);
      }
    };
    fallback();
    // Intentional: re-evaluate when type maps hydrate so unresolved names trigger once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, globalActivityTypes, activityTypes, hasAttemptedGlobalFallback]);

  const unifiedMediaSlides = useMemo((): MediaSlide[] => {
    const slides: MediaSlide[] = [];

    mediaList.forEach((imageUrl) => {
      slides.push({ type: "image", content: imageUrl });
    });

    if (hasSessionDetails && !performanceLog) {
      slides.push({
        type: "session",
        content: {
          measurements: session!.ActivityMeasurementList!,
        },
      });
    }

    // Admins always see GPS when present (monitoring overrides privacy).
    if (hasGPSData && activityLog) {
      slides.push({ type: "gps", content: activityLog });
    }

    if (hasHeartRateData && activityLog) {
      slides.push({ type: "heartRate", content: activityLog });
    }

    return slides;
  }, [
    mediaList,
    hasSessionDetails,
    performanceLog,
    session,
    hasGPSData,
    hasHeartRateData,
    activityLog,
  ]);

  const hasTrainingMetrics =
    (typeof post.duration === "number" && post.duration > 0) ||
    post.load !== undefined ||
    post.AverageHR !== undefined;

  useEffect(() => {
    if (!showComments || !post.CommentsList?.length) return;

    const uniqueIds = post.CommentsList.map((c) => c.userId).filter(
      (id, index, arr) => id && arr.indexOf(id) === index,
    );

    let cancelled = false;
    (async () => {
      const next: Record<string, { username: string; imageUrl?: string }> = {};
      await Promise.all(
        uniqueIds.map(async (userId) => {
          try {
            const publicData = await getUserPublicData(userId);
            next[userId] = {
              username: publicData?.username || `User ${userId.slice(-4)}`,
              imageUrl: publicData?.imageUrl,
            };
          } catch {
            next[userId] = { username: `User ${userId.slice(-4)}` };
          }
        }),
      );
      if (!cancelled) {
        setCommentProfiles((prev) => ({ ...prev, ...next }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showComments, post.CommentsList]);

  const shellClass = embedded
    ? "bg-base-100 w-full"
    : "bg-base-100 shadow-sm mb-2 w-full mx-auto rounded-xl overflow-hidden border border-base-200";

  const currentSlide = unifiedMediaSlides[currentImageIndex];
  const currentImageUrl =
    currentSlide?.type === "image" ? currentSlide.content : null;

  return (
    <div className={shellClass}>
      {/* Header */}
      <div className="flex pb-0 p-4 items-center justify-between mb-3">
        <div className="flex items-center gap-2 p-1">
          <div className="w-6 h-6 rounded-full overflow-hidden bg-neutral flex items-center justify-center">
            {post.userImageUrl ? (
              <img
                src={post.userImageUrl}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                alt={`${post.username || "User"} avatar`}
              />
            ) : (
              <span className="text-xs font-semibold font-grotesk text-neutral-content">
                {post.username?.charAt(0).toUpperCase() || "?"}
              </span>
            )}
          </div>
          <span className="text-xs font-medium text-base-content/80">
            {post.username || "User"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {post.isPrivate && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-base-200 text-base-content/80 border border-base-300">
              Private
            </span>
          )}
          {post.gpsDataIsPrivate && hasGPSData && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-700 border border-blue-200">
              GPS Private
            </span>
          )}
          <p className="text-xs text-base-content/50 font-grotesk">
            {formatDate(post.postTimestamp)}
          </p>
        </div>
      </div>

      {/* Notes */}
      {post.notes && (
        <div className="mb-3 pl-4 pr-4">
          <p className="text-sm text-base-content/80 whitespace-pre-wrap leading-relaxed">
            {isTextExpanded ? (
              <>
                {post.notes}
                {shouldTruncateText(post.notes) && (
                  <button
                    type="button"
                    onClick={() => setIsTextExpanded(false)}
                    className="ml-1 text-xs text-base-content/60 hover:text-base-content/80 font-medium transition-colors font-grotesk lowercase"
                  >
                    read less
                  </button>
                )}
              </>
            ) : (
              <>
                {getTruncatedText(post.notes)}
                {shouldTruncateText(post.notes) && (
                  <>
                    <span className="text-base-content/60">...</span>
                    <button
                      type="button"
                      onClick={() => setIsTextExpanded(true)}
                      className="ml-1 text-xs text-base-content/60 hover:text-base-content/80 font-medium transition-colors font-grotesk lowercase"
                    >
                      read more
                    </button>
                  </>
                )}
              </>
            )}
          </p>
        </div>
      )}

      {/* Unified media carousel */}
      {linkedLoading && unifiedMediaSlides.length === 0 && (
        <div className="mb-3 px-4">
          <div className="h-40 rounded-md bg-base-200 animate-pulse" />
        </div>
      )}

      {unifiedMediaSlides.length > 0 && (
        <div className="mb-3 relative">
          <div
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            onScroll={(e) => {
              const container = e.currentTarget;
              const slideWidth = container.offsetWidth - 32;
              if (slideWidth <= 0) return;
              const newIndex = Math.round(container.scrollLeft / slideWidth);
              if (
                newIndex !== currentImageIndex &&
                newIndex >= 0 &&
                newIndex < unifiedMediaSlides.length
              ) {
                setCurrentImageIndex(newIndex);
              }
            }}
          >
            {unifiedMediaSlides.map((slide, index) => (
              <div
                key={`${slide.type}-${index}`}
                className="flex-shrink-0 snap-start"
                style={{ width: "calc(100% - 0.2rem)" }}
              >
                {slide.type === "gps" ? (
                  <GpsRouteSlide activityLog={slide.content} />
                ) : slide.type === "heartRate" ? (
                  <HeartRateZonesSlide activityLog={slide.content} />
                ) : slide.type === "session" ? (
                  <SessionDetailsSlide
                    measurements={slide.content.measurements}
                    getActivityName={getActivityTypeName}
                  />
                ) : (
                  <button
                    type="button"
                    className="w-full text-left"
                    onClick={() => {
                      setCurrentImageIndex(index);
                      setViewerOpen(true);
                    }}
                  >
                    <div className="relative w-full h-80 md:h-96 overflow-hidden rounded-md bg-base-200">
                      <img
                        src={slide.content}
                        alt={`Post media ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </button>
                )}
              </div>
            ))}
          </div>

          {unifiedMediaSlides.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10 pointer-events-none">
              {unifiedMediaSlides.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentImageIndex
                      ? "w-6 bg-neutral"
                      : "w-6 bg-base-100/50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Training metrics */}
      {hasTrainingMetrics && (
        <div className="flex pl-4 pr-4 gap-2 mb-3">
          {typeof post.duration === "number" && post.duration > 0 && (
            <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
              <div className="text-xs text-base-content/60">Duration</div>
              <div className="text-sm font-semibold font-heading text-base-content">
                {formatDuration(post.duration)}
              </div>
            </div>
          )}
          {post.load !== undefined && (
            <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
              <div className="text-xs text-base-content/60">Load</div>
              <div className="text-sm font-semibold font-heading text-base-content">
                {Math.round(post.load)}
              </div>
            </div>
          )}
          {post.AverageHR !== undefined && (
            <div className="flex-1 bg-base-200 rounded-md p-2 text-center">
              <div className="text-xs text-base-content/60">Avg HR</div>
              <div className="text-sm font-semibold font-heading text-base-content">
                {Math.round(post.AverageHR)} bpm
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance log */}
      {performanceLog &&
        (() => {
          const logActivity = performanceLog.activity;
          const firstActivity =
            !logActivity && session
              ? (session.ActivityMeasurementList?.find(
                  (m) => m.timeLogged !== undefined,
                ) ?? session.ActivityMeasurementList?.[0])
              : null;

          const activityName = logActivity
            ? logActivity.activityName
            : firstActivity
              ? getActivityTypeName(firstActivity)
              : performanceLog.performanceTitle;

          const loggedVariables = (
            logActivity
              ? logActivity.variables
              : firstActivity
                ? firstActivity.variables
                : []
          ).filter(
            (v) => v.measurement !== null && v.measurement !== undefined,
          );

          return (
            <div className="mx-4 mb-3 bg-amber-50 rounded-lg p-3">
              <p className="text-xs font-semibold tracking-widest text-amber-700/70 uppercase mb-1.5">
                Performance
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-neutral-900 font-heading">
                  {activityName}
                </span>
                {loggedVariables.length > 0 ? (
                  loggedVariables.map((v) => (
                    <span key={v.name} className="flex items-baseline gap-1">
                      <span className="text-neutral-900 font-medium">
                        {v.measurement}
                      </span>
                      {v.unit && (
                        <span className="text-neutral-500 text-sm">
                          {v.unit}
                        </span>
                      )}
                    </span>
                  ))
                ) : !logActivity && !firstActivity ? (
                  <>
                    <span className="text-neutral-900">
                      {performanceLog.performanceResult}
                    </span>
                    <span className="text-neutral-500 text-sm">units</span>
                  </>
                ) : null}
                {performanceLog.tag && performanceLog.tag.length > 0 && (
                  <div className="flex gap-1">
                    {performanceLog.tag.map((t) => (
                      <span
                        key={t}
                        className="px-1.5 py-0.5 text-xs font-bold bg-amber-200 text-amber-800 rounded"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {performanceLog.notes ? (
                <p className="text-sm text-neutral-500 mt-1">
                  {performanceLog.notes}
                </p>
              ) : null}
            </div>
          );
        })()}

      {/* Likes & comments — display only */}
      <div className="px-4 py-3 border-t border-base-200">
        <div
          className={`flex items-center justify-between${showComments ? " mb-3" : ""}`}
        >
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-base-content/80 select-none"
            aria-label={`${post.likesList?.length || 0} likes`}
          >
            {(post.likesList?.length || 0) > 0 ? (
              <HeartSolidIcon className="w-5 h-5 text-red-600" />
            ) : (
              <HeartIcon className="w-5 h-5" />
            )}
            <span>{post.likesList?.length || 0}</span>
          </div>

          <button
            type="button"
            onClick={() => setShowComments((prev) => !prev)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-base-content/80 hover:text-base-content transition-colors"
            aria-label="Toggle comments"
          >
            <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
            <span>{post.CommentsList?.length || 0}</span>
          </button>
        </div>

        {showComments && (
          <div className="pt-2">
            {post.CommentsList?.length ? (
              <div className="space-y-3">
                {post.CommentsList.map((comment) => {
                  const profile = commentProfiles[comment.userId];
                  const username = profile?.username || "User";
                  const avatar = profile?.imageUrl;
                  return (
                    <div key={comment.commentId} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full overflow-hidden bg-neutral flex-shrink-0 flex items-center justify-center">
                        {avatar ? (
                          <img
                            src={avatar}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs font-semibold text-neutral-content">
                            {username.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-base-content">
                            {username}
                          </span>
                          <span className="text-xs text-base-content/40">
                            {formatRelativeTime(comment.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-base-content/80 whitespace-pre-wrap">
                          {comment.text}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-base-content/50 text-center py-2">
                No comments
              </p>
            )}
          </div>
        )}
      </div>

      {viewerOpen &&
        currentImageUrl &&
        createPortal(
          <div
            className="fixed inset-0 z-[999999] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setViewerOpen(false)}
          >
            <button
              type="button"
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
              onClick={() => setViewerOpen(false)}
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <img
              src={currentImageUrl}
              alt=""
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default AdminPostCard;
